import React from "react";

import type { CompanionPlane } from "../../atoms/hangingProtocolAtoms";
import { PLANE_COLORS_HEX } from "../../constants/planeColors";

interface CompanionPlaneToggleProps {
  plane: CompanionPlane;
  onChange: (plane: CompanionPlane) => void;
}

// Plane color from the shared SSOT (matches the 3D viewer's legend):
// coronal = green, sagittal = red.
const OPTIONS: { value: CompanionPlane; label: string; color: string }[] = [
  { value: "Coronal", label: "Coronal", color: PLANE_COLORS_HEX.coronal },
  { value: "Sagittal", label: "Sagittal", color: PLANE_COLORS_HEX.sagittal },
];

/**
 * Small segmented control overlaid on the top-right companion pane in the
 * "seg-2up" layout. Switches which plane (coronal ⇄ sagittal) is shown there;
 * the other plane is then not mounted (so it is not seeded by nnInteractive).
 *
 * Each option carries its plane color (matching the 3D viewer's legend) so the
 * annotator can tell at a glance which plane the companion pane is showing.
 *
 * `stopPropagation` on mousedown/click keeps the toggle's pointer events from
 * bubbling up to the slot wrapper — defensive so a click here can never be read
 * as a segmentation/measurement interaction on the pane below.
 */
export const CompanionPlaneToggle: React.FC<CompanionPlaneToggleProps> = ({ plane, onChange }) => {
  return (
    <div
      className="absolute top-2 left-1/2 z-30 flex -translate-x-1/2 items-center gap-0.5 rounded bg-bg-2/90 px-0.5 py-0.5 ring-1 ring-border backdrop-blur"
      onMouseDown={(e) => e.stopPropagation()}
    >
      {OPTIONS.map((opt) => {
        const active = plane === opt.value;
        return (
          <button
            key={opt.value}
            type="button"
            onMouseDown={(e) => e.stopPropagation()}
            onClick={(e) => {
              e.stopPropagation();
              onChange(opt.value);
            }}
            className={`flex items-center gap-1 rounded px-2 py-0.5 text-[10px] font-medium transition-colors ${
              active ? "ring-1" : "text-fg-muted hover:bg-white/10 hover:text-white"
            }`}
            style={
              active
                ? {
                    color: opt.color,
                    // 22 ≈ 13% alpha bg tint, 80 ≈ 50% alpha ring, in #rrggbbaa.
                    backgroundColor: `${opt.color}22`,
                    boxShadow: `inset 0 0 0 1px ${opt.color}80`,
                  }
                : undefined
            }
            title={`Show ${opt.label} companion`}
          >
            <span
              className="inline-block h-1.5 w-1.5 rounded-full"
              style={{ backgroundColor: opt.color, opacity: active ? 1 : 0.5 }}
            />
            {opt.label}
          </button>
        );
      })}
    </div>
  );
};

export default CompanionPlaneToggle;
